import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axiosInstance from '../../../shared/services/api/axiosInstance';
import EventsTable from '../components/EventsTable';
import EventsFilterBar from '../components/EventsFilterBar';
import ColumnConfigModal from '../components/ColumnConfigModal';
import { preloadPropertyDiscovery } from '../components/EventPropertyFilterModule';
import { usePreferences } from '../../../shared/context/preferencesContext';

const Events = () => {
  const params = useParams();
  const [events, setEvents] = useState([]);
  const [totalEvents, setTotalEvents] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [selectedTeam, setSelectedTeam] = useState(localStorage.getItem("selectedTeam") || '');
  const [searchTerm, setSearchTerm] = useState('');
  const [timeFilter, setTimeFilter] = useState(null);
  const [selectedTimePreset, setSelectedTimePreset] = useState('today');
  const [lastRefreshTime, setLastRefreshTime] = useState(null);
  const [modularFilters, setModularFilters] = useState([]);
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const { loadEventsTableColumns } = usePreferences();
  
  // Track latest request and cancel in-flight ones to avoid stale updates
  const latestRequestRef = useRef(0);
  const abortControllerRef = useRef(null);

  // Handle search
  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentOffset(0);
    setEvents([]);
    setHasMore(true);
    // Pass the search term directly to avoid state timing issues
    fetchEvents(0, true, term);
  };

  // Handle time filter change
  const handleTimeFilterChange = (filter) => {
    setTimeFilter(filter);
    setSelectedTimePreset(filter.type === 'preset' ? filter.preset : 'custom');
    setCurrentOffset(0);
    setEvents([]);
    setTotalEvents(0); // Reset total when time filter changes
    setHasMore(true);
    // Pass filter directly to avoid state timing issues
    fetchEvents(0, true, null, null, filter);
  };

  // Handle refresh
  const handleRefresh = () => {
    setLastRefreshTime(new Date());
    setCurrentOffset(0);
    setEvents([]);
    setHasMore(true);
    fetchEvents(0, true);
  };

  // Handle load more
  const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      fetchEvents(currentOffset, false);
    }
  };

  // Handle modular filters change
  const handleModularFiltersChange = (filters) => {
    console.log('handleModularFiltersChange called with:', filters, 'Filter count:', filters.length);
    // Update state
    setModularFilters(filters);
    setCurrentOffset(0);
    setEvents([]);
    setHasMore(true);
    // Pass filters directly to fetchEvents to avoid stale state issues
    fetchEvents(0, true, null, filters);
  };

  // Convert time filter to modularFilters format for API
  const buildTimeFilters = (timeFilterOverride = null) => {
    const filterToUse = timeFilterOverride !== null ? timeFilterOverride : timeFilter;
    
    if (!filterToUse) {
      // Default to today if no filter set
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      return [{
        field: 'timestamp',
        operator: 'between',
        value: today.toISOString(),
        value2: now.toISOString()
      }];
    }

    const filters = [];
    
    if (filterToUse.type === 'preset' || filterToUse.type === 'custom') {
      const fromDate = new Date(filterToUse.from);
      const toDate = new Date(filterToUse.to);

      if (filterToUse.enableTimeRanges) {
        // Use exact time ranges
        filters.push({
          field: 'timestamp',
          operator: 'gte',
          value: fromDate.toISOString()
        });
        filters.push({
          field: 'timestamp',
          operator: 'lte',
          value: toDate.toISOString()
        });
      } else {
        // Use between for full-day inclusion
        filters.push({
          field: 'timestamp',
          operator: 'between',
          value: fromDate.toISOString(),
          value2: toDate.toISOString()
        });
      }
    }

    return filters;
  };

  // Fetch events - all events for team, no website filter
  const fetchEvents = async (offset = 0, isReset = false, searchTermOverride = null, filtersOverride = null, timeFilterOverride = null) => {
    let requestId = 0;
    try {
      // Generate a unique request id and cancel any in-flight request
      requestId = ++latestRequestRef.current;
      if (abortControllerRef.current) {
        try { abortControllerRef.current.abort(); } catch (_) {}
      }
      abortControllerRef.current = new AbortController();

      if (isReset) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      setError(null);
      
      const teamId = selectedTeam || localStorage.getItem("selectedTeam");
      if (!teamId) {
        if (isReset) {
          setLoading(false);
        } else {
          setLoadingMore(false);
        }
        return;
      }

      // Calculate page number from offset (25 events per page)
      const page = Math.floor(offset / 25) + 1;

      // Fetch all events for team - no website_id filter
      const params = new URLSearchParams({
        team_id: teamId,
        page: page.toString(),
        limit: '25'
      });

      // Add search term - use override if provided, otherwise use state
      const currentSearchTerm = searchTermOverride !== null ? searchTermOverride : searchTerm;
      if (currentSearchTerm && currentSearchTerm.trim()) {
        params.set('search', currentSearchTerm.trim());
      }

      // Combine time filters with modular filters
      const timeFilters = buildTimeFilters(timeFilterOverride);
      const allFilters = [...timeFilters];
      
      // Use filtersOverride if provided, otherwise use state
      const currentModularFilters = filtersOverride !== null ? filtersOverride : modularFilters;
      
      // Add modular filters (convert to API format)
      if (currentModularFilters && currentModularFilters.length > 0) {
        console.log('Processing modularFilters:', currentModularFilters);
        const apiFilters = currentModularFilters
          .filter(f => {
            // Filter out invalid filters
            if (!f.field || !f.operator) return false;
            
            // Operators that don't need values
            const noValueOperators = ['is_set', 'is_not_set', 'is_true', 'is_false'];
            if (noValueOperators.includes(f.operator)) {
              return true; // These operators don't need values
            }
            
            // For operators that need values
            if (f.value === null || f.value === '') return false;
            
            // For array values, ensure it's not empty
            if (Array.isArray(f.value)) {
              return f.value.length > 0;
            }
            
            return true;
          })
          .map(filter => {
            const apiFilter = {
              field: filter.field,
              operator: filter.operator
            };
            
            // Preserve event-specific properties (critical for backend filtering)
            if (filter.eventName) {
              apiFilter.eventName = filter.eventName;
            }
            if (filter.category) {
              apiFilter.category = filter.category;
            }
            if (filter.eventId) {
              apiFilter.eventId = filter.eventId;
            }
            
            // Preserve list type properties (critical for list filtering)
            if (filter.propertyType) {
              apiFilter.propertyType = filter.propertyType;
            }
            if (filter.listMode) {
              apiFilter.listMode = filter.listMode;
            }
            if (filter.listItemType) {
              apiFilter.listItemType = filter.listItemType;
            }
            
            // Operators that don't need values
            const noValueOperators = ['is_set', 'is_not_set', 'is_true', 'is_false'];
            if (noValueOperators.includes(filter.operator)) {
              // These operators don't need values
              apiFilter.value = null;
            } else if (filter.operator === 'between' || filter.operator === 'not_between') {
              apiFilter.value = filter.value;
              apiFilter.value2 = filter.value2;
            } else if (Array.isArray(filter.value)) {
              // Preserve 'includes' and 'excludes' operators, and other array operators
              if (['includes', 'excludes', 'is', 'is_not'].includes(filter.operator)) {
                apiFilter.operator = filter.operator; // Keep original operator
              } else {
                apiFilter.operator = filter.operator === 'not_in' ? 'not_in' : 'in';
              }
              apiFilter.value = filter.value;
              console.log(`Filter for ${filter.field}: ${apiFilter.operator} with values:`, apiFilter.value);
            } else {
              apiFilter.value = filter.value;
            }
            
            return apiFilter;
          });
        allFilters.push(...apiFilters);
        console.log('All filters to send to API:', allFilters);
      }
      
      if (allFilters.length > 0) {
        params.set('modularFilters', encodeURIComponent(JSON.stringify(allFilters)));
      }

      const response = await axiosInstance.get(`/events?${params.toString()}`, {
        signal: abortControllerRef.current.signal
      });

      // Ignore if a newer request has been issued
      if (requestId !== latestRequestRef.current) {
        return;
      }

      if (response.data.success) {
        const eventsData = response.data.events || [];
        const pagination = response.data.pagination || {};
        
        if (isReset) {
          setEvents(eventsData);
          // Preload property discovery for first event so filter popup opens faster
          if (eventsData.length > 0) {
            const teamIdForPreload = selectedTeam || localStorage.getItem('selectedTeam');
            if (teamIdForPreload) {
              preloadPropertyDiscovery(teamIdForPreload, eventsData[0].event_name);
            }
          }
        } else {
          // Append new events to existing ones
          setEvents(prev => [...prev, ...eventsData]);
        }
        
        setTotalEvents(pagination.total || 0);
        const newOffset = offset + eventsData.length;
        setCurrentOffset(newOffset);
        
        // Check if there are more events to load
        const totalLoaded = isReset ? eventsData.length : events.length + eventsData.length;
        setHasMore(totalLoaded < (pagination.total || 0) && eventsData.length === 25);
      } else {
        if (isReset) {
          setEvents([]);
        }
        setTotalEvents(0);
        setHasMore(false);
      }
    } catch (err) {
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') {
        return;
      }
      console.error('Error fetching events:', err);
      setError(err.response?.data?.error || 'Failed to fetch events');
      if (isReset) {
        setEvents([]);
      }
      setTotalEvents(0);
      setHasMore(false);
    } finally {
      if (latestRequestRef.current === requestId) {
        if (isReset) {
          setLoading(false);
        } else {
          setLoadingMore(false);
        }
      }
    }
  };

  // Load preferences when team changes
  useEffect(() => {
    if (selectedTeam) {
      loadEventsTableColumns(selectedTeam);
    }
  }, [selectedTeam, loadEventsTableColumns]);

  // Fetch events when team, search term, or time filter changes (initial load)
  useEffect(() => {
    if (selectedTeam && currentOffset === 0 && !loading) {
      fetchEvents(0, true);
    }
  }, [selectedTeam]);

  // Initialize with today's filter on mount (only once)
  const hasInitialized = useRef(false);
  useEffect(() => {
    if (!hasInitialized.current && !timeFilter && selectedTeam) {
      hasInitialized.current = true;
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      handleTimeFilterChange({
        type: 'preset',
        preset: 'today',
        from: today.toISOString(),
        to: now.toISOString()
      });
    }
  }, [selectedTeam]);

  // Update document title when search is active
  useEffect(() => {
    if (searchTerm) {
      document.title = `Search: "${searchTerm}" - Events | Cryptique`;
    } else {
      document.title = 'Events | Cryptique';
    }
  }, [searchTerm]);

  // Check if team is selected
  if (!selectedTeam) {
    return (
      <div className="flex-grow overflow-y-auto p-6">
        <div className="bg-white border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-[#caa968] rounded-full flex items-center justify-center">
                <svg className="h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
            <div className="ml-3">
              <p className="text-sm text-[#1d0c46] font-medium" style={{ fontFamily: 'Raleway, sans-serif' }}>
                Please select a team to view events.
              </p>
              <p className="text-xs text-[#475569] mt-1">
                You can select a team from the sidebar or settings.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-1 overflow-hidden relative flex-col">
      {/* Scrollable content area - filter bar and table together */}
      <div className="flex-1 overflow-y-auto">
        {/* Filter Bar */}
        <EventsFilterBar
          onRefresh={handleRefresh}
          onTimeFilterChange={handleTimeFilterChange}
          onSearchChange={handleSearch}
          searchTerm={searchTerm}
          selectedTimePreset={selectedTimePreset}
          lastRefreshTime={lastRefreshTime}
          teamId={selectedTeam}
          onFiltersChange={handleModularFiltersChange}
          onEditColumns={() => setIsConfigModalOpen(true)}
        />

        {/* Table */}
        <div className="p-6">
          <EventsTable 
            events={events}
            loading={loading}
            loadingMore={loadingMore}
            error={error}
            totalEvents={totalEvents}
            hasMore={hasMore}
            onLoadMore={handleLoadMore}
          />
        </div>
      </div>

      {/* Column Configuration Modal */}
      <ColumnConfigModal
        isOpen={isConfigModalOpen}
        onClose={() => setIsConfigModalOpen(false)}
        teamId={selectedTeam}
      />
    </div>
  );
};

export default Events;
